import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogRef,
} from '@angular/material/dialog';
import { EjournalApiService } from '@et/api';
import { PresentationalAtomsModule } from '@et/presentational/atoms';
import { catchError, finalize } from 'rxjs';
import { EjournalService } from '../../services/ejournal.service';
import { EtJournalEntry, EtJournalEntryWithId } from '@et/typings';

/** Dialog data for {@link JournalEntryAddSupplementalComponent}. */
export type JournalEntryAddSupplementalData = {
  entryId: string;
};

@Component({
  selector: 'et-journal-entry-add-supplemental',
  standalone: true,
  imports: [PresentationalAtomsModule, ReactiveFormsModule, MatDialogClose],
  templateUrl: './journal-entry-add-supplemental.component.html',
  styleUrl: './journal-entry-add-supplemental.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JournalEntryAddSupplementalComponent {
  private data = inject<JournalEntryAddSupplementalData>(MAT_DIALOG_DATA);
  private dialogRef = inject(
    MatDialogRef<JournalEntryAddSupplementalComponent>,
  );
  private readonly ejournalApiService = inject(EjournalApiService);
  private readonly ejournalService = inject(EjournalService);

  error = signal<string | null>(null);
  isLoading = signal(false);

  private fb = inject(FormBuilder);

  readonly form = this.fb.nonNullable.group({
    note: ['', Validators.required],
  });

  /**
   * Validates the form, posts the supplemental note, updates local entry state, and closes the dialog.
   */
  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const note = this.form.value.note!;
    this.error.set(null);
    this.isLoading.set(true);
    this.ejournalApiService
      .addSupplementalEntry(this.data.entryId, note)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        catchError((err) => {
          const errMst = err.error.title || 'Failed to add supplemental entry';
          this.error.set(errMst);
          throw err;
        }),
      )
      .subscribe((entry) => {
        this.ejournalService.updateEntries((entries) => {
          const index = entries.findIndex(
            (e) => e.eJournalEntryId === entry.id,
          );
          if (index !== -1) {
            entries[index] = this.withIdToEntry(entry);
          }
          return [...entries];
        });
        this.dialogRef.close(true);
      });
  }

  /**
   * Maps API shape (`id`) to the UI `EtJournalEntry` shape (`eJournalEntryId`).
   *
   * @param w - Entry payload including `id` from the API.
   * @returns Entry with `eJournalEntryId` set from `id`.
   */
  private withIdToEntry(w: EtJournalEntryWithId): EtJournalEntry {
    const { id, ...rest } = w;
    return { ...rest, eJournalEntryId: id };
  }
}
